import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    Container, Box, Typography, Button, TextField,
    Alert, Paper, Avatar, InputAdornment, IconButton, useTheme, useMediaQuery, Stack, Chip
} from '@mui/material';
import BurbujasLoader from '../UIElements/BurbujasLoader';
import {
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    Person as PersonIcon,
    Cancel as CancelIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    EventBusy as EventBusyIcon,
    Block as QrCodeOffIcon,
    Facebook as FacebookIcon,
    X as XIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    TouchApp as TouchAppIcon
} from '@mui/icons-material';
import { getPersonaCidi, confirmarCcAsistencia, getCcAsistenciaEventoById } from '../../services/cc_asistencia.service.js';
import logoCba from '../imagenes/gobierno_blanco.png';

export default function RegistroCcAsistencia() {
    const { eventoId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [evento, setEvento] = useState(null);
    const [loadingEvento, setLoadingEvento] = useState(true);
    const [error, setError] = useState('');

    const [cuil, setCuil] = useState('');
    const [participantInfo, setParticipantInfo] = useState(null);
    const [loadingCuil, setLoadingCuil] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [eventoValido, setEventoValido] = useState(true);
    const [qrActivo, setQrActivo] = useState(true);
    const [cupoLleno, setCupoLleno] = useState(false);
    const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);


    useEffect(() => {
        const fetchEvento = async () => {
            try {
                const data = await getCcAsistenciaEventoById(eventoId);
                const today = dayjs().format('YYYY-MM-DD');
                if (data.fecha !== today) {
                    setEventoValido(false);
                }
                if (data.estado_qr === 0) {
                    setQrActivo(false);
                }
                if (data.cupo > 0 && data.cantidad_asistidos >= data.cupo) {
                    setCupoLleno(true);
                }
                setEvento(data);
            } catch (err) {
                setError('Evento no encontrado o ha ocurrido un error.');
            } finally {
                setLoadingEvento(false);
            }
        };
        fetchEvento();
    }, [eventoId]);

    const handleSearch = async () => {
        if (!cuil || cuil.length !== 11) {
            setError('Por favor, ingresa un CUIL válido (11 dígitos, sin guiones).');
            return;
        }

        setLoadingCuil(true);
        setError('');
        setParticipantInfo(null);
        try {
            const data = await getPersonaCidi(cuil);
            setParticipantInfo({
                cuil: cuil,
                nombre: data.nombre,
                apellido: data.apellido
            });
        } catch (err) {
            setError('No se pudo encontrar el participante en CIDI.');
        } finally {
            setLoadingCuil(false);
        }
    };

    const handleConfirm = async () => {
        try {
            setLoadingCuil(true);
            setError('');

            // Llamada directa con fetch para evitar que apiClient intercepte 403/401
            const url = process.env.REACT_APP_API_URL + '/cc-asistencias/inscriptos/confirmar';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ cuil: participantInfo.cuil, evento_id: evento.id })
            });

            const resultData = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (resultData.code === 'QR_DISABLED') {
                    setQrActivo(false);
                    setParticipantInfo(null);
                    return;
                }
                if (resultData.code === 'CUPO_LLENO') {
                    setCupoLleno(true);
                    setParticipantInfo(null);
                    return;
                }
                throw new Error(resultData.message || 'Error al registrar la asistencia.');
            }

            if (resultData.code === 'ALREADY_REGISTERED') {
                setIsAlreadyRegistered(true);
                return;
            }

            setSuccessMessage(`¡Asistencia registrada exitosamente!`);
        } catch (err) {
            const backendError = err.response?.data?.message || err.message;
            setError(backendError || 'Ocurrió un error al registrar la asistencia.');
        } finally {
            setLoadingCuil(false);
        }
    };



    if (!evento && !loadingEvento) return <Container maxWidth="xs" sx={{ mt: 8 }}>{error && <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>}</Container>;

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const partes = fecha.split('-');
        if (partes.length === 3) return `${partes[2]}-${partes[1]}-${partes[0]}`;
        return fecha;
    };

    const formatearHorario = (texto) => {
        if (!texto) return '';
        let procesado = texto.toLowerCase();
        procesado = procesado.replace(/\bhs?\.?\b/g, '').trim();
        procesado = procesado.replace(/\b(\d{1,2})(?::(\d{2}))?\b/g, (match, hora, min) => {
            const h = hora.padStart(2, '0');
            const m = min || '00';
            return `${h}:${m}`;
        });
        procesado = procesado.replace(/\s+/g, ' ').trim();
        return procesado ? `${procesado}` : '';
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            minHeight: '100dvh', // Dynamic viewport height for modern browsers
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#F8FAFC'
        }}>
            <Container
                maxWidth="xs"
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center', // Horizontal centering
                    py: { xs: 2, sm: 4 }, // Slightly less padding on mobile
                    px: 2
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '24px',
                        width: '100%',
                        overflow: 'hidden',
                        boxShadow: '0 12px 48px rgba(0,0,0,0.08)',
                        border: '1px solid #E2E8F0',
                        bgcolor: '#fff'
                    }}
                >
                    <Box sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center', minHeight: { xs: 'auto', sm: '400px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>

                        {loadingEvento ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <BurbujasLoader />
                            </Box>
                        ) : (
                            <>
                                {!eventoValido && (
                                    <>
                                        <Avatar sx={{ mx: 'auto', mb: 3, width: 80, height: 80, bgcolor: '#ffebe9', color: 'error.main', boxShadow: '0 8px 16px rgba(0,0,0,0.05)' }}>
                                            <EventBusyIcon sx={{ fontSize: 40 }} />
                                        </Avatar>

                                        <Typography variant="h5" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A', mb: 1 }}>
                                            Registro no disponible
                                        </Typography>

                                        <Typography variant="h6" sx={{ fontFamily: 'Poppins', color: 'primary.main', fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
                                            {evento.curso?.nombre}
                                        </Typography>

                                        <Alert severity="error" sx={{ mt: 3, mb: 3, borderRadius: '12px', textAlign: 'left', fontFamily: 'Poppins' }}>
                                            No es posible confirmar la asistencia en este momento. La fecha del evento <strong>({formatearFecha(evento.fecha)})</strong> no coincide con la fecha actual.
                                        </Alert>
                                    </>
                                )}

                                {eventoValido && !qrActivo && (
                                    <>
                                        <Avatar sx={{ mx: 'auto', mb: 3, width: 80, height: 80, bgcolor: '#FFF3E0', color: '#F57C00', boxShadow: '0 8px 16px rgba(0,0,0,0.05)' }}>
                                            <QrCodeOffIcon sx={{ fontSize: 40 }} />
                                        </Avatar>

                                        <Typography variant="h5" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A', mb: 1 }}>
                                            QR no activo
                                        </Typography>

                                        <Typography variant="h6" sx={{ fontFamily: 'Poppins', color: 'primary.main', fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
                                            {evento.curso?.nombre}
                                        </Typography>

                                        <Alert severity="warning" sx={{ mt: 3, mb: 3, borderRadius: '12px', textAlign: 'left', fontFamily: 'Poppins' }}>
                                            El registro de asistencia por QR no está habilitado en este momento. Consultá con el docente o responsable del evento.
                                        </Alert>

                                        <Button
                                            variant="outlined"
                                            onClick={() => window.location.reload()}
                                            fullWidth
                                            sx={{ fontFamily: 'Geogrotesque Sharp', borderRadius: '12px', py: 1.5, fontWeight: 'bold', fontSize: '1rem', color: 'warning.main', borderColor: 'warning.main' }}
                                        >
                                            REINTENTAR
                                        </Button>
                                    </>
                                )}

                                {eventoValido && qrActivo && cupoLleno && (
                                    <>
                                        <Avatar sx={{ mx: 'auto', mb: 3, width: 80, height: 80, bgcolor: '#FFF3E0', color: '#F57C00', boxShadow: '0 8px 16px rgba(0,0,0,0.05)' }}>
                                            <QrCodeOffIcon sx={{ fontSize: 40 }} />
                                        </Avatar>

                                        <Typography variant="h5" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A', mb: 1 }}>
                                            Cupo lleno
                                        </Typography>

                                        <Typography variant="h6" sx={{ fontFamily: 'Poppins', color: 'primary.main', fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
                                            {evento.curso?.nombre}
                                        </Typography>

                                        <Alert severity="warning" sx={{ mt: 3, mb: 3, borderRadius: '12px', textAlign: 'left', fontFamily: 'Poppins' }}>
                                            Se ha alcanzado el cupo máximo permitido para este evento. No es posible registrar nuevas asistencias.
                                        </Alert>

                                        <Button
                                            variant="outlined"
                                            onClick={() => window.location.reload()}
                                            fullWidth
                                            sx={{ fontFamily: 'Geogrotesque Sharp', borderRadius: '12px', py: 1.5, fontWeight: 'bold', fontSize: '1rem', color: 'warning.main', borderColor: 'warning.main' }}
                                        >
                                            REINTENTAR
                                        </Button>
                                    </>
                                )}

                                {eventoValido && qrActivo && isAlreadyRegistered && (
                                    <>
                                        <Avatar sx={{ mx: 'auto', mb: 3, width: 80, height: 80, bgcolor: '#FFF3E0', color: '#F57C00', boxShadow: '0 8px 16px rgba(0,0,0,0.05)' }}>
                                            <QrCodeOffIcon sx={{ fontSize: 40 }} />
                                        </Avatar>

                                        <Typography variant="h5" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A', mb: 1 }}>
                                            Asistencia ya registrada
                                        </Typography>

                                        <Typography variant="h6" sx={{ fontFamily: 'Poppins', color: 'primary.main', fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
                                            {participantInfo?.nombre} {participantInfo?.apellido}
                                        </Typography>

                                        <Alert severity="warning" sx={{ mt: 3, mb: 3, borderRadius: '12px', textAlign: 'left', fontFamily: 'Poppins' }}>
                                            Usted ya registró su asistencia previamente para el curso <strong>{evento.curso?.nombre}</strong>.
                                        </Alert>
                                    </>
                                )}

                                {eventoValido && qrActivo && !cupoLleno && !isAlreadyRegistered && !successMessage && !participantInfo && (
                                    <>
                                        <Avatar sx={{ mx: 'auto', mb: 3, width: 80, height: 80, bgcolor: 'primary.light', color: 'primary.main', boxShadow: '0 8px 16px rgba(0,0,0,0.05)' }}>
                                            <AssignmentTurnedInIcon sx={{ fontSize: 40 }} />
                                        </Avatar>

                                        <Typography variant="h5" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A', mb: 1 }}>
                                            Paso 1 de 2: Buscar participante
                                        </Typography>

                                        <Typography variant="h6" sx={{ fontFamily: 'Poppins', color: 'primary.main', fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
                                            {evento.curso?.nombre}
                                        </Typography>

                                        <Typography variant="body1" sx={{ fontFamily: 'Poppins', color: '#64748B', mb: 4 }}>
                                            📅 {formatearFecha(evento.fecha)} <br /> 🕒 {evento.horario_inicio?.substring(0, 5)} a {evento.horario_fin?.substring(0, 5)} hs
                                        </Typography>

                                        <Box sx={{ mb: 3 }}>
                                            <TextField
                                                placeholder="Ingresa tu CUIL"
                                                variant="outlined"
                                                fullWidth
                                                autoFocus
                                                value={cuil}
                                                onChange={(e) => setCuil(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                                disabled={loadingCuil}
                                                helperText="11 dígitos, sin guiones"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PersonIcon sx={{ color: '#94A3B8' }} />
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        fontFamily: 'Poppins',
                                                        borderRadius: '12px',
                                                        fontSize: '1.2rem',
                                                        fontWeight: 600,
                                                        letterSpacing: 2,
                                                        bgcolor: '#F8FAFC',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                        '&:hover fieldset': { borderColor: 'primary.main' }
                                                    }
                                                }}
                                                FormHelperTextProps={{ sx: { fontFamily: 'Poppins', mt: 1, fontWeight: 500 } }}
                                            />
                                        </Box>

                                        {error && <Alert severity="error" sx={{ borderRadius: '12px', mb: 3, textAlign: 'left' }}>{error}</Alert>}

                                        <Button
                                            variant="contained"
                                            onClick={handleSearch}
                                            disabled={loadingCuil || cuil.length !== 11}
                                            fullWidth
                                            sx={{
                                                fontFamily: 'Geogrotesque Sharp',
                                                borderRadius: '12px',
                                                py: 2,
                                                fontWeight: 'bold',
                                                fontSize: '1.1rem',
                                                boxShadow: '0 4px 12px rgba(0,123,255,0.2)'
                                            }}
                                        >
                                            {loadingCuil ? <BurbujasLoader small /> : 'BUSCAR DATOS'}
                                        </Button>
                                    </>
                                )}

                                {eventoValido && qrActivo && !successMessage && !isAlreadyRegistered && participantInfo && (
                                    <>
                                        <Alert
                                            severity="warning"
                                            icon={false}
                                            sx={{
                                                mb: { xs: 1.5, sm: 2 },
                                                p: { xs: 1, sm: 1.25 },
                                                borderRadius: '14px',
                                                textAlign: 'center',
                                                fontFamily: 'Poppins',
                                                bgcolor: '#FFFBEB',
                                                border: '2px solid #FCD34D',
                                                color: '#92400E',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                '& .MuiAlert-message': {
                                                    width: '100%',
                                                    p: 0,
                                                    textAlign: 'center'
                                                }
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'Poppins', fontSize: { xs: '0.82rem', sm: '0.92rem' } }}>
                                                ⚠️ ¡PASO FINAL: PRESIONÁ EL BOTÓN VERDE ABAJO!
                                            </Typography>
                                        </Alert>

                                        <Typography variant="h6" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A', mb: 0.2, fontSize: { xs: '1.15rem', sm: '1.35rem' } }}>
                                            Paso 2 de 2: Confirmación
                                        </Typography>

                                        <Typography variant="body2" sx={{ fontFamily: 'Poppins', color: '#64748B', mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.78rem', sm: '0.875rem' } }}>
                                            Verificá tus datos antes de presionar el botón de confirmación.
                                        </Typography>

                                        <Paper
                                            elevation={0}
                                            sx={{
                                                bgcolor: '#F8FAFC',
                                                p: { xs: 1.75, sm: 2.5 },
                                                borderRadius: '16px',
                                                mb: { xs: 2, sm: 2.5 },
                                                border: '1px solid #E2E8F0',
                                                textAlign: 'left'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="caption" sx={{ fontFamily: 'Poppins', color: '#64748B', fontWeight: 600, letterSpacing: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                    DATOS DEL PARTICIPANTE
                                                </Typography>
                                                <Chip
                                                    label="Verificado en CiDi"
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.7rem' }, height: 20 }}
                                                />
                                            </Box>

                                            <Typography variant="caption" sx={{ fontFamily: 'Poppins', color: '#64748B', fontWeight: 500, fontSize: '0.7rem', display: 'block' }}>
                                                NOMBRE Y APELLIDO
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: 'primary.main', mb: 1, fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1.2 }}>
                                                {participantInfo.nombre} {participantInfo.apellido}
                                            </Typography>

                                            <Typography variant="caption" sx={{ fontFamily: 'Poppins', color: '#64748B', fontWeight: 500, fontSize: '0.7rem', display: 'block' }}>
                                                CUIL
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontFamily: 'Poppins', fontWeight: 600, color: '#1A1A1A', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
                                                {participantInfo.cuil}
                                            </Typography>
                                        </Paper>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5 } }}>
                                            <Button
                                                variant="contained"
                                                onClick={handleConfirm}
                                                disabled={loadingCuil}
                                                fullWidth
                                                startIcon={!loadingCuil && <TouchAppIcon sx={{ fontSize: { xs: '22px !important', sm: '26px !important' } }} />}
                                                sx={{
                                                    fontFamily: 'Geogrotesque Sharp',
                                                    borderRadius: '14px',
                                                    py: { xs: 1.5, sm: 1.75 },
                                                    px: 2,
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '1rem', sm: '1.15rem' },
                                                    bgcolor: '#32A430',
                                                    color: '#FFFFFF',
                                                    letterSpacing: 0.5,
                                                    animation: 'pulseGreen 2s infinite ease-in-out',
                                                    '@keyframes pulseGreen': {
                                                        '0%': { transform: 'scale(1)', boxShadow: '0 4px 14px rgba(50, 164, 48, 0.4)' },
                                                        '50%': { transform: 'scale(1.015)', boxShadow: '0 6px 20px rgba(50, 164, 48, 0.65)' },
                                                        '100%': { transform: 'scale(1)', boxShadow: '0 4px 14px rgba(50, 164, 48, 0.4)' }
                                                    },
                                                    '&:hover': {
                                                        bgcolor: '#288826',
                                                        boxShadow: '0 8px 24px rgba(40, 136, 38, 0.6)'
                                                    }
                                                }}
                                            >
                                                {loadingCuil ? <BurbujasLoader small /> : '¡CONFIRMAR MI ASISTENCIA AHORA!'}
                                            </Button>

                                            <Button
                                                variant="text"
                                                onClick={() => setParticipantInfo(null)}
                                                disabled={loadingCuil}
                                                fullWidth
                                                sx={{
                                                    fontFamily: 'Geogrotesque Sharp',
                                                    color: '#64748B',
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                                    py: { xs: 0.75, sm: 1.25 }
                                                }}
                                            >
                                                CANCELAR Y VOLVER
                                            </Button>
                                        </Box>
                                    </>
                                )}

                                {eventoValido && qrActivo && successMessage && !isAlreadyRegistered && (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                            <CheckCircleIcon sx={{ fontSize: 100, color: 'success.main' }} />
                                        </Box>

                                        <Typography variant="h4" sx={{ fontFamily: 'Geogrotesque Sharp', fontWeight: 'bold', color: '#1A1A1A', mb: 2 }}>
                                            ¡Registro Exitoso!
                                        </Typography>

                                        <Typography variant="h6" sx={{ fontFamily: 'Poppins', color: 'primary.main', fontWeight: 600, mb: 2 }}>
                                            {participantInfo?.nombre} {participantInfo?.apellido}
                                        </Typography>

                                        <Typography variant="body1" sx={{ fontFamily: 'Poppins', color: '#64748B', mb: 4, lineHeight: 1.6 }}>
                                            Tu asistencia ha sido registrada correctamente para:<br />
                                            <strong>{evento.curso?.nombre}</strong>
                                        </Typography>
                                    </>
                                )}

                                {eventoValido && qrActivo && error && participantInfo && !successMessage && !isAlreadyRegistered && (
                                    <Box sx={{ px: 4, pb: 4 }}>
                                        <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Paper>
            </Container>

            {/* Footer Institucional Transparente Vertical para Mobile */}
            <Box
                component="footer"
                sx={{
                    backgroundColor: "transparent",
                    color: "#64748B",
                    width: "100%",
                    py: 4,
                    mt: 'auto',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="xs">
                    <Stack spacing={1} alignItems="center">
                        {/* Logo solicitado: gobierno_blanco.png con filtro para gris oscuro */}
                        <Box
                            component="img"
                            src={logoCba}
                            alt="Gobierno de Córdoba"
                            sx={{
                                width: '220px',
                                height: "auto",
                                // Aplicamos brillo 0 y opacidad baja para lograr un gris institucional
                                filter: 'brightness(0) opacity(0.6)',
                                mb: 1 // Pequeño espacio positivo hacia abajo
                            }}
                        />

                        {/* Redes Sociales en Gris */}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                sx={{ color: '#636465' }}
                                component="a"
                                href="https://es-la.facebook.com/gobdecordoba/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FacebookIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                sx={{ color: '#636465' }}
                                component="a"
                                href="https://x.com/gobdecordoba?lang=es"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <XIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                sx={{ color: '#636465' }}
                                component="a"
                                href="https://www.instagram.com/cordobaok/?hl=es-la"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <InstagramIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                sx={{ color: '#636465' }}
                                component="a"
                                href="https://www.youtube.com/user/gobiernodecordoba"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <YouTubeIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Container>
            </Box>


        </Box>
    );
}
